import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './useAuth';
import { useResultsPool } from './useResultsPool';

/**
 * Jobs Data Management Hook
 * 
 * Provides real-time jobs data management with:
 * - Real-time subscriptions
 * - Results pooling
 * - Provider grouping
 * - Status tracking
 * - Performance optimization
 */

export const useOrderJobsData = ({
  orderIds = [],
  autoSubscribe = true,
  groupByProvider = true,
  userScope = 'current_user' // 'all' | 'current_user' | 'other_users'
} = {}) => {
  const { userId, isLoading: authLoading } = useAuth();
  
  // State management
  const [state, setState] = useState({
    jobs: [],
    jobsByOrderId: {},
    jobsByProvider: {},
    loading: false,
    error: null,
    lastUpdated: null
  });

  const unsubscribeRef = useRef(null);
  const subscribedOrderIdsRef = useRef([]);
  const subscribeToJobsRef = useRef(null);
  const groupByProviderRef = useRef(groupByProvider);
  
  // Update the ref when the prop changes
  groupByProviderRef.current = groupByProvider;

  // Get current job IDs for results pooling
  const currentJobIds = useMemo(() => 
    state.jobs.map(job => job.id).filter(Boolean),
    [state.jobs]
  );

  // Use results pool for efficient results loading
  const { results: pooledResults } = useResultsPool(currentJobIds);

  // Subscribe to jobs for given order IDs
  const subscribeToJobs = useCallback((orderIdsToSubscribe) => {
    if (!userId || authLoading || orderIdsToSubscribe.length === 0) return;

    // Check if we're already subscribed to these orders
    const currentIds = subscribedOrderIdsRef.current.sort().join(',');
    const newIds = orderIdsToSubscribe.sort().join(',');
    if (currentIds === newIds && unsubscribeRef.current) return;

    // Unsubscribe from previous subscription
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Create query for jobs
      let constraints = [
        where('orderId', 'in', orderIdsToSubscribe.slice(0, 10)), // Firestore limit
        orderBy('createdAt', 'desc')
      ];
      
      // Apply user scope filtering
      if (userScope === 'current_user') {
        constraints.unshift(where('userId', '==', userId));
      } else if (userScope === 'other_users') {
        constraints.unshift(where('userId', '!=', userId));
      }
      // For 'all' scope, no user filtering is applied
      
      const jobsQuery = query(collection(db, 'jobs'), ...constraints);

      // Subscribe to real-time updates
      const unsubscribe = onSnapshot(jobsQuery, 
        (snapshot) => {
          const jobs = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          // Process jobs inline to avoid dependency issues
          // Group by order ID
          const jobsByOrderId = jobs.reduce((acc, job) => {
            if (!acc[job.orderId]) acc[job.orderId] = [];
            acc[job.orderId].push(job);
            return acc;
          }, {});

          // Group by provider (if enabled)
          const jobsByProvider = groupByProviderRef.current ? jobs.reduce((acc, job) => {
            const provider = job.provider || 'unknown';
            if (!acc[provider]) acc[provider] = [];
            acc[provider].push(job);
            return acc;
          }, {}) : {};

          const processedData = {
            jobs,
            jobsByOrderId,
            jobsByProvider,
            lastUpdated: Date.now()
          };

          setState(prev => ({
            ...prev,
            ...processedData,
            loading: false,
            error: null
          }));

          console.log(`📊 Jobs updated: ${jobs.length} jobs for ${orderIdsToSubscribe.length} orders`);
        },
        (error) => {
          console.error('Error in jobs subscription:', error);
          setState(prev => ({
            ...prev,
            loading: false,
            error: error.message
          }));
        }
      );

      unsubscribeRef.current = unsubscribe;
      subscribedOrderIdsRef.current = orderIdsToSubscribe;

    } catch (error) {
      console.error('Error setting up jobs subscription:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  }, [userId, authLoading, userScope]); // Added userScope dependency

  // Store the latest subscribeToJobs function in a ref
  subscribeToJobsRef.current = subscribeToJobs;

  // Auto-subscribe when order IDs change
  useEffect(() => {
    // Manual subscription trigger to avoid infinite loop
    if (autoSubscribe && orderIds.length > 0) {
      // Use a timeout to break the synchronous loop
      const timeoutId = setTimeout(() => {
        if (subscribeToJobsRef.current) {
          console.log('🔄 Manual subscription triggered for orderIds:', orderIds);
          subscribeToJobsRef.current(orderIds);
        }
      }, 0);
      
      return () => {
        clearTimeout(timeoutId);
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
      };
    }
    
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [autoSubscribe, orderIds.join(',')]); // Use join to avoid array recreation issues

  // Manual subscription control
  const subscribe = useCallback((newOrderIds) => {
    if (subscribeToJobsRef.current) {
      subscribeToJobsRef.current(newOrderIds);
    }
  }, []); // Use ref to avoid dependency issues

  const unsubscribe = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
      subscribedOrderIdsRef.current = [];
      setState(prev => ({ 
        ...prev, 
        jobs: [], 
        jobsByOrderId: {}, 
        jobsByProvider: {} 
      }));
    }
  }, []);

  // Get jobs for specific order
  const getJobsForOrder = useCallback((orderId) => {
    return state.jobsByOrderId[orderId] || [];
  }, [state.jobsByOrderId]);

  // Get jobs by provider
  const getJobsByProvider = useCallback((provider) => {
    return state.jobsByProvider[provider] || [];
  }, [state.jobsByProvider]);

  // Get job by ID
  const getJobById = useCallback((jobId) => {
    return state.jobs.find(job => job.id === jobId);
  }, [state.jobs]);

  // Get jobs with results
  const getJobsWithResults = useCallback(() => {
    return state.jobs.filter(job => pooledResults[job.id]);
  }, [state.jobs, pooledResults]);

  // Statistics
  const stats = useMemo(() => {
    const jobs = state.jobs;
    const providers = Object.keys(state.jobsByProvider);
    
    return {
      total: jobs.length,
      byStatus: {
        pending: jobs.filter(j => j.status === 'pending').length,
        processing: jobs.filter(j => j.status === 'processing').length,
        completed: jobs.filter(j => j.status === 'completed').length,
        failed: jobs.filter(j => j.status === 'failed').length
      },
      byProvider: providers.reduce((acc, provider) => {
        acc[provider] = state.jobsByProvider[provider].length;
        return acc;
      }, {}),
      withResults: Object.keys(pooledResults).length,
      totalCost: jobs.reduce((sum, job) => sum + (job.cost || 0), 0),
      avgProcessingTime: jobs.length > 0 ? 
        jobs.reduce((sum, job) => {
          if (job.completedAt && job.startedAt) {
            return sum + (job.completedAt.toDate() - job.startedAt.toDate());
          }
          return sum;
        }, 0) / jobs.filter(j => j.completedAt && j.startedAt).length : 0
    };
  }, [state.jobs, state.jobsByProvider, pooledResults]);

  // Enhanced job data with results
  const jobsWithResults = useMemo(() => {
    return state.jobs.map(job => ({
      ...job,
      results: pooledResults[job.id] || null,
      hasResults: !!pooledResults[job.id]
    }));
  }, [state.jobs, pooledResults]);

  // Provider summary
  const providerSummary = useMemo(() => {
    return Object.entries(state.jobsByProvider).map(([provider, jobs]) => ({
      provider,
      jobCount: jobs.length,
      completedCount: jobs.filter(j => j.status === 'completed').length,
      failedCount: jobs.filter(j => j.status === 'failed').length,
      totalCost: jobs.reduce((sum, job) => sum + (job.cost || 0), 0),
      avgCost: jobs.length > 0 ? 
        jobs.reduce((sum, job) => sum + (job.cost || 0), 0) / jobs.length : 0,
      resultsCount: jobs.filter(j => pooledResults[j.id]).length
    }));
  }, [state.jobsByProvider, pooledResults]);

  return {
    // Data
    jobs: state.jobs,
    jobsWithResults,
    jobsByOrderId: state.jobsByOrderId,
    jobsByProvider: state.jobsByProvider,
    results: pooledResults,
    stats,
    providerSummary,
    
    // State
    loading: state.loading,
    error: state.error,
    lastUpdated: state.lastUpdated,
    isSubscribed: !!unsubscribeRef.current,
    subscribedOrderIds: subscribedOrderIdsRef.current,
    
    // Actions
    subscribe,
    unsubscribe,
    
    // Utilities
    getJobsForOrder,
    getJobsByProvider,
    getJobById,
    getJobsWithResults,
    
    // Metadata
    isReady: !authLoading && !!userId
  };
};
